import { ChangeDetectionStrategy, Component, input, Input, output, ViewEncapsulation } from '@angular/core';
import {
    IItemsListFilter,
    ISelectedFilterItem,
} from '@fi-shared/fi-items-list/fi-items-list-filter/fi-items-list-filter/fi-items-list-filter';
import { IAccessRightFilterCollection } from '@fi-shared/models';
import { FiFilterType } from '@fi-shared/fi-items-list/fi-items-list-filter/fi-filter-type-enum';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { ISideListOptionsItem } from '@fi-shared/fi-items-list/fi-items-list';
import { BehaviorSubject } from 'rxjs';
import { FiItemsListFilterHelperService } from '@fi-shared/fi-items-list/fi-items-list-filter/fi-items-list-filter-helper.service';

@Component({
    selector: 'fi-items-list-filter-settings-details',
    templateUrl: './fi-items-list-filter-settings-details.component.html',
    styleUrls: ['./fi-items-list-filter-settings-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-items-list-filter-settings-details', '[class.expanded]': 'isFilterSettingsShown()' },
    standalone: false,
})
export class FiItemsListFilterSettingsDetailsComponent {
    @Input() accessRightFilterCollection: IAccessRightFilterCollection;
    @Input() currentFilter: IItemsListFilter;
    @Input() selectedFilterItems: ISelectedFilterItem[] = [];
    @Input() itemsCountText: string;
    @Input() itemsListType: FiItemsListType;
    @Input() savedFilters: IItemsListFilter[] = [];

    public onClearAll = output<void>();
    public onSaveCurrentFilter = output<void>();
    public filterSettingsChanged = output<IItemsListFilter>();

    public onOpenRenameSavedFilterDialog = output<IItemsListFilter>();
    public onOpenChangeSavedFilterDialog = output<IItemsListFilter>();
    public onOpenDeleteSavedFilterDialog = output<IItemsListFilter>();
    public onSelectSavedFilter = output<IItemsListFilter>();

    @Input() categoryItems: ISideListOptionsItem[];
    @Input() changedItems: ISideListOptionsItem[];
    @Input() tagItems: ISideListOptionsItem[];

    public isFilterSettingsShown = input<boolean>(false);

    accessRightFilterChange$ = new BehaviorSubject<FiFilterType>(undefined);

    constructor(private fiItemsListFilterHelperService: FiItemsListFilterHelperService) {}

    toggleFilterSettingsDataItem(filterSettingsItem: { itemId: number; filterType: FiFilterType }) {
        const filterSettingsData = this.getFilterSettingsDataByFilterType(filterSettingsItem.filterType);
        filterSettingsData.includes(filterSettingsItem.itemId)
            ? filterSettingsData.splice(filterSettingsData.indexOf(filterSettingsItem.itemId), 1)
            : filterSettingsData.push(filterSettingsItem.itemId);
    }

    private getFilterSettingsDataByFilterType(filterType: FiFilterType): number[] {
        switch (filterType) {
            case FiFilterType.Category:
                return this.currentFilter.Settings.CategoryFilterTypes;
            case FiFilterType.Tag:
                return this.currentFilter.Settings.TagIds;
            case FiFilterType.User:
                return this.currentFilter.Settings.UserIds;
            case FiFilterType.BusinessUnit:
                return this.currentFilter.Settings.BusinessUnitIds;
            case FiFilterType.Area:
                return this.currentFilter.Settings.AreaIds;
            case FiFilterType.Cluster:
                return this.currentFilter.Settings.ClusterIds;
            case FiFilterType.BusinessArea:
                return this.currentFilter.Settings.BusinessAreaIds;
        }
    }

    removeSelectedFilterItem(filterItem: ISelectedFilterItem) {
        const selectedFilterItemIndex = this.selectedFilterItems.findIndex((item: ISelectedFilterItem) => {
            return this.fiItemsListFilterHelperService.isFilterItemSelected(item, filterItem);
        });
        this.selectedFilterItems.splice(selectedFilterItemIndex, 1);
        if (filterItem.filterType === FiFilterType.CustomDate) {
            this.currentFilter.Settings.ChangedOnRange = undefined;
        } else if (filterItem.filterType === FiFilterType.LastEdit) {
            this.currentFilter.Settings.ChangedOnFilterType = undefined;
        } else {
            this.toggleFilterSettingsDataItem({
                itemId: filterItem.value as number,
                filterType: filterItem.filterType,
            });
        }
        this.selectedFilterItems = [...this.selectedFilterItems];
        this.accessRightFilterChange$.next(filterItem.filterType);
        this.onFilterSettingsChanged();
    }

    clearAll() {
        this.onClearAll.emit();
        this.accessRightFilterChange$.next(undefined);
    }

    saveCurrentFilter() {
        this.onSaveCurrentFilter.emit();
    }

    onFilterSettingsChanged() {
        this.filterSettingsChanged.emit(this.currentFilter);
    }

    openRenameSavedFilterDialog(savedFilterData: IItemsListFilter) {
        this.onOpenRenameSavedFilterDialog.emit(savedFilterData);
    }

    openChangeSavedFilterDialog(savedFilterData: IItemsListFilter) {
        this.onOpenChangeSavedFilterDialog.emit(savedFilterData);
    }

    openDeleteSavedFilterDialog(savedFilterData: IItemsListFilter) {
        this.onOpenDeleteSavedFilterDialog.emit(savedFilterData);
    }

    selectSavedFilter(savedFilter: IItemsListFilter) {
        this.onSelectSavedFilter.emit(savedFilter);
        this.accessRightFilterChange$.next(FiFilterType.User);
        this.accessRightFilterChange$.next(FiFilterType.BusinessUnit);
        this.accessRightFilterChange$.next(FiFilterType.Area);
        this.accessRightFilterChange$.next(FiFilterType.BusinessArea);
    }
}
